<script setup>
import { defineAsyncComponent, ref, shallowRef, computed, watch, onBeforeMount } from 'vue'
import LoadingShadowInput1qppView from '@/components/desktop/common/loading/shadow-1qpp-view/LoadingShadowInput1qppView.vue'

import { useInputPageCommonStore } from '@/stores/input/input-page-common.store.js'
import { useModalStore } from '@/stores/common/modal.store.js'
import { useErrorsStore } from '@/stores/common/errors.store.js'
import { useTimetableStore } from '@/stores/common/timetable.store.js'
import { useFunctionalitiesStore } from '@/stores/common/functionalities.store.js'
import { useInputPageInitializer } from '@/composables/input/use-input-page-initializer.js'
import { usePopState } from '@/composables/common/use-pop-state.js'

import ABTests from '@/application/utilities/ab-tests.js'
import { isUserAnAgent } from '@/application/utilities/customer-check24-functions.js'
import { initChattyButton } from '@/application/utilities/chatty/chatty.js'
import { useTheBotStore } from '@/stores/the-bot/the-bot.store'
import { navigationUtils } from '@/application/utilities/navigation-utils.js'
import dataLayer from '@/application/utilities/data-layer.js'

import {
  KEY_REFERRER_PAGE,
  PAGE_INPUT_ID,
  ERROR_LOADING_INPUT_DATA,
  KEY_LAST_SELECTED_SORT,
  KEY_LAST_SELECTED_ISSUING_EXPERIENCES,
  KEY_LAST_SELECTED_COVERAGES,
  KEY_LAST_SELECTED_GRADE
} from '@/config/common/common.config.js'
import { INPUT_PAGE_1QPP } from '@/config/input/input-common.config.js'

const ResumeView = defineAsyncComponent(() => import('@/components/desktop/resume/ResumeView.vue'))
const InputFormView = defineAsyncComponent(() => import('@/components/desktop/input/input-form/InputFormView.vue'))
const Input1qppView = defineAsyncComponent(() => import('@/components/desktop/input/input-1qpp/Input1qppView.vue'))
const UserAgentLine = defineAsyncComponent(() => import('@/components/desktop/common/UserAgentLine'))

const inputPageCommonStore = useInputPageCommonStore()
const modalStore = useModalStore()
const errorsStore = useErrorsStore()
const timetableStore = useTimetableStore()
const functionalitiesStore = useFunctionalitiesStore()
const theBotStore = useTheBotStore()

const {
  lastComparisons,
  checkResumeViewVisibility,
  selectInputPageVersion,
  initInputPage
} = useInputPageInitializer()

const isVisibleUserAgentLine = ref(false)

// Resume view
const isVisibleResumeView = ref(null)
const startNewComparison = ref(false)

function validateComparison (referenceCode) {
  navigationUtils.redirectToValidationPage(referenceCode)
}

function recoveryComparison (referenceCode) {
  navigationUtils.redirectToRecoveryPage(referenceCode)
}

function startNewComparisonFromResumeView () {
  isVisibleResumeView.value = false
  startNewComparison.value = true
  sessionStorage.removeItem(KEY_LAST_SELECTED_SORT)
  sessionStorage.removeItem(KEY_LAST_SELECTED_ISSUING_EXPERIENCES)
  sessionStorage.removeItem(KEY_LAST_SELECTED_COVERAGES)
  sessionStorage.removeItem(KEY_LAST_SELECTED_GRADE)
}

// Input view
const inputViewComponent = shallowRef(null)

const isVisibleInputView = computed(() => {
  return isVisibleResumeView.value === false
})

watch(isVisibleInputView, (newValue) => {
  inputPageCommonStore.isLoadingInputPage = false

  if (newValue) {
    setupInputPage()
  }
})

async function setupInputPage () {
  inputPageCommonStore.isLoadingInputView = true

  const abTests = new ABTests()
  await Promise.all([
    timetableStore.getTimetableCheck24(),
    functionalitiesStore.initFunctionalities(),
    abTests.initialize()
  ])

  try {
    prepareInputView()
    inputPageCommonStore.buildQuestionsState()
    await initInputPage(startNewComparison.value)
    await inputPageCommonStore.hydrateQuestionsState()
    inputPageCommonStore.isLoadingInputView = false
  } catch (error) {
    errorsStore.setError(ERROR_LOADING_INPUT_DATA, error)
  }
}

function prepareInputView () {
  const inputVersion = selectInputPageVersion()
  dataLayer.addInputPageVersion(inputVersion)

  inputPageCommonStore.inputPageVersion = inputVersion
  inputViewComponent.value = inputVersion === INPUT_PAGE_1QPP ? Input1qppView : InputFormView
}

async function initAssistantChat () {
  await theBotStore.initInput()

  if (theBotStore.isTheBotActive) {
    return
  }

  await initChattyButton(PAGE_INPUT_ID)
}

async function init () {
  inputPageCommonStore.isLoadingInputPage = true
  inputPageCommonStore.checkBrowserBack()
  isVisibleUserAgentLine.value = isUserAnAgent()
  isVisibleResumeView.value = await checkResumeViewVisibility()

  if (isVisibleResumeView.value) {
    inputPageCommonStore.isLoadingInputPage = false
    inputPageCommonStore.isLoadingInputView = false
  }

  sessionStorage.removeItem(KEY_REFERRER_PAGE)

  await initAssistantChat()
}

usePopState()

init()

onBeforeMount(() => {
  dataLayer.addInputPageGeneralTrackingValues()
})
</script>

<template>
  <div class="c24es-input-page">
    <UserAgentLine
      v-if="isVisibleUserAgentLine"
    />

    <LoadingShadowInput1qppView
      v-show="inputPageCommonStore.isVisibleLoadingShadow"
    />
    <section v-show="!inputPageCommonStore.isVisibleLoadingShadow">
      <ResumeView
        v-if="isVisibleResumeView"
        :last-comparisons="lastComparisons"
        @validate-comparison="validateComparison"
        @new-comparison="startNewComparisonFromResumeView"
        @recovery-comparison="recoveryComparison"
      />

      <component
        :is="inputViewComponent"
        v-if="isVisibleInputView"
      />
    </section>

    <component
      :is="modalStore.modalComponent"
    />
  </div>
</template>

<style scoped lang="scss">
.c24es-input-page {
  background-color: var(--color-white);
}
</style>
